import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useEntendaCalculoAccess from '../hooks/useEntendaCalculoAccess';
import useAuditLog from '../hooks/useAuditLog';
import { DictionaryProvider } from '../contexts/DictionaryContext';
import { buildCurrentReturnPath, persistPostAuthPath } from '../utils/postAuthNavigation';

import AuthPage from '../pages/AuthPage';
import LoadingState from '../components/common/LoadingState';
import Layout from '../components/layout/Layout';
import TermoResponsabilidadeGate from '../components/auth/TermoResponsabilidadeGate';

const CHUNK_RETRY_PARAM = 'sisfol_chunk_retry';

const isChunkLoadError = (error) => {
  const text = String(error?.name || '') + ' ' + String(error?.message || error || '');
  return /ChunkLoadError|Loading chunk .* failed|Failed to fetch dynamically imported module|Unexpected token ['"]?<['"]?/i.test(text);
};

const clearChunkRetryMarker = () => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has(CHUNK_RETRY_PARAM)) return;
  url.searchParams.delete(CHUNK_RETRY_PARAM);
  window.history.replaceState(window.history.state, '', url.toString());
};

const recoverChunkOnce = (error) => {
  if (typeof window === 'undefined' || !isChunkLoadError(error)) throw error;

  const url = new URL(window.location.href);
  if (url.searchParams.has(CHUNK_RETRY_PARAM) || window.__sisfolChunkReloadRequested) throw error;

  window.__sisfolChunkReloadRequested = true;
  url.searchParams.set(CHUNK_RETRY_PARAM, String(Date.now()));
  window.location.replace(url.toString());

  // Mantém o Suspense ativo durante a navegação e evita enviar ao Error Boundary
  // uma falha que já está sendo recuperada por recarga da versão atual.
  return new Promise(() => {});
};

const lazyLoad = (importer) => {
  return lazy(() => importer().then((module) => {
    clearChunkRetryMarker();
    return { default: module.default || Object.values(module)[0] };
  }).catch(recoverChunkOnce));
};

const VinculoSelectionPage = lazyLoad(() => import('../pages/VinculoSelectionPage'));
const PWAInstallPage = lazyLoad(() => import('../pages/PWAInstallPage'));
const Dashboard = lazyLoad(() => import('../pages/Dashboard'));
const ChangePasswordPage = lazyLoad(() => import('../pages/ChangePasswordPage'));
const RecadastramentoPage = lazyLoad(() => import('../pages/RecadastramentoPage'));
const ContrachequePage = lazyLoad(() => import('../pages/ContrachequePage'));
const RequerimentosPage = lazyLoad(() => import('../pages/RequerimentosPage'));
const HelpCenterPage = lazyLoad(() => import('../pages/HelpCenterPage'));
const EntendaSeuCalculoPage = lazyLoad(() => import('../pages/EntendaSeuCalculoPage'));
const AvisosPage = lazyLoad(() => import('../pages/AvisosPage'));
const NotificationsPage = lazyLoad(() => import('../pages/NotificationsPage'));
const FichaFuncionalPage = lazyLoad(() => import('../pages/FichaFuncionalPage'));

const normalizeAuditPath = (pathname = '/') => String(pathname || '/')
  .replace(/\/\d{3,}(?=\/|$)/g, '/:id')
  .replace(/\/[0-9a-f]{8}-[0-9a-f-]{27,}(?=\/|$)/gi, '/:id')
  .replace(/\/[A-Za-z0-9_-]{20,}(?=\/|$)/g, '/:id');

const RouteAuditLogger = () => {
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const { logAction } = useAuditLog();
  const lastLoggedRef = React.useRef('');

  React.useEffect(() => {
    if (!isAuthenticated || !user) return;
    if (['/login', '/instalar'].includes(location.pathname)) return;

    const safePath = normalizeAuditPath(location.pathname);
    const key = `${user.uid || user.id || 'usuario'}|${safePath}|${location.search || ''}`;
    if (lastLoggedRef.current === key) return;
    lastLoggedRef.current = key;

    Promise.resolve(logAction('PAGE_VIEW', {
      entityType: 'ROUTE',
      entityId: safePath,
      modulo: 'Navegação',
      pagina: safePath,
      rota: safePath,
      tela: safePath,
      tipoAcao: 'consulta',
      alterouDados: false,
      descricao: 'Servidor acessou uma página do Portal do Servidor.',
    })).catch(() => {});
  }, [isAuthenticated, location.pathname, location.search, logAction, user]);

  return null;
};

const FullPageLoader = ({ text = 'Carregando módulo...' }) => (
  <LoadingState label={text} description="Preparando esta área do SISDP." mode="page" />
);
const EntendaCalculoRouteContent = () => {
  const { enabled, loading } = useEntendaCalculoAccess();

  if (loading) {
    return <FullPageLoader text="Conferindo liberação do cálculo..." />;
  }

  if (!enabled) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout><EntendaSeuCalculoPage /></Layout>;
};


const ProtectedRoute = ({ children, requireVinculo = true }) => {
  const { isAuthenticated, authReady, loading, user, vinculos, vinculoSelecionado } = useAuth();
  const location = useLocation();

  if (loading || (isAuthenticated && !authReady)) {
    return <FullPageLoader text="Conferindo sua sessão..." />;
  }

  if (!isAuthenticated) {
    const returnTo = persistPostAuthPath(buildCurrentReturnPath(location));
    const loginParams = new URLSearchParams();
    if (returnTo) loginParams.set('returnTo', returnTo);
    return <Navigate to={`/login?${loginParams.toString()}`} replace />;
  }

  if (user?.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }


  const termoResponsabilidadeAtual =
    user?.termoResponsabilidadeAtual || null;

  const termoResponsabilidadePendente = Boolean(
    !user?.mustChangePassword &&
      termoResponsabilidadeAtual?.versaoId &&
      String(termoResponsabilidadeAtual?.status || '').toLowerCase() ===
        'publicado' &&
      (String(user?.termoResponsabilidadeAceitoVersaoId || '') !==
        String(termoResponsabilidadeAtual.versaoId) ||
        String(user?.termoResponsabilidadeAceitoHashConteudo || '') !==
          String(termoResponsabilidadeAtual.hashConteudo || ''))
  );

  if (termoResponsabilidadePendente) {
    return (
      <TermoResponsabilidadeGate
        termoAtual={termoResponsabilidadeAtual}
      />
    );
  }

  const hasMultipleVinculos = Array.isArray(vinculos) && vinculos.length > 1;
  const hasSelectedVinculo = !!vinculoSelecionado;

  if (requireVinculo && hasMultipleVinculos && !hasSelectedVinculo && location.pathname !== '/selecionar-vinculo') {
    return <Navigate to="/selecionar-vinculo" replace />;
  }

  return <DictionaryProvider>{children}</DictionaryProvider>;
};

const AppRouter = () => {
  return (
    <Suspense fallback={<FullPageLoader />}>
      <RouteAuditLogger />
      <Routes>
        <Route path="/login" element={<AuthPage />} />
        <Route path="/instalar" element={<PWAInstallPage />} />

        <Route
          path="/change-password"
          element={<ProtectedRoute requireVinculo={false}><ChangePasswordPage /></ProtectedRoute>}
        />

        <Route
          path="/selecionar-vinculo"
          element={<ProtectedRoute requireVinculo={false}><VinculoSelectionPage /></ProtectedRoute>}
        />

        <Route
          path="/dashboard"
          element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>}
        />

        <Route
          path="/recadastramento"
          element={<ProtectedRoute><Layout><RecadastramentoPage /></Layout></ProtectedRoute>}
        />

        <Route
          path="/contracheques/:matricula"
          element={<ProtectedRoute><Layout><ContrachequePage /></Layout></ProtectedRoute>}
        />

        <Route
          path="/contracheques/:matricula/calculo"
          element={<ProtectedRoute><EntendaCalculoRouteContent /></ProtectedRoute>}
        />

        <Route
          path="/requerimentos"
          element={<ProtectedRoute><Layout><RequerimentosPage /></Layout></ProtectedRoute>}
        />

        <Route
          path="/notificacoes"
          element={<ProtectedRoute><Layout><NotificationsPage /></Layout></ProtectedRoute>}
        />

        <Route
          path="/avisos"
          element={<ProtectedRoute><Layout><AvisosPage /></Layout></ProtectedRoute>}
        />

        <Route
          path="/ajuda"
          element={<ProtectedRoute><Layout><HelpCenterPage /></Layout></ProtectedRoute>}
        />

        <Route
          path="/ficha-funcional"
          element={<ProtectedRoute><Layout><FichaFuncionalPage /></Layout></ProtectedRoute>}
        />

        <Route
          path="/"
          element={<ProtectedRoute><Navigate to="/dashboard" replace /></ProtectedRoute>}
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};

export default AppRouter;
